// Miglioriamo il posizionamento del dropdown con un approccio più avanzato
// Modifica al componente DocumentFilters.tsx

import React, { useState, useRef, useEffect } from "react";
import { DocumentStatus, DocumentType } from "../../types/Document";
import Select from "components/ui/Select";

interface DocumentFiltersProps {
  statusFilter: string;
  typeFilter: string;
  dateFilter: string;
  onStatusChange: (status: string) => void;
  onTypeChange: (type: string) => void;
  onDateChange: (date: string) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
}

const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  statusFilter,
  typeFilter,
  dateFilter,
  onStatusChange,
  onTypeChange,
  onDateChange,
  onApplyFilters,
  onResetFilters,
}) => {
  const [filterDropdownVisible, setFilterDropdownVisible] =
    useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  // Gestisce la direzione del dropdown (verso l'alto o verso il basso)
  const [dropdownDirection, setDropdownDirection] = useState<"down" | "up">(
    "down"
  );

  // Funzione per calcolare la direzione del dropdown
  const calculateDropdownDirection = () => {
    if (dropdownRef.current && dropdownContentRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownContentRef.current.offsetHeight;
      const viewportHeight = window.innerHeight;

      // Se lo spazio sotto il pulsante è insufficiente ma c'è spazio sopra
      if (
        buttonRect.bottom + dropdownHeight > viewportHeight &&
        buttonRect.top > dropdownHeight
      ) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    }
  };

  // Effect per calcolare la direzione quando il dropdown viene mostrato
  useEffect(() => {
    if (filterDropdownVisible) {
      calculateDropdownDirection();
      // Ricalcola la direzione se la finestra viene ridimensionata
      window.addEventListener("resize", calculateDropdownDirection);
    }

    return () => {
      window.removeEventListener("resize", calculateDropdownDirection);
    };
  }, [filterDropdownVisible]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setFilterDropdownVisible(false);
      }
    }

    if (filterDropdownVisible) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [filterDropdownVisible]);

  const getFilterSummary = () => {
    const filters = [];

    if (statusFilter) {
      filters.push(`Stato: ${getStatus(statusFilter)}`);
    }

    if (typeFilter) {
      filters.push(`Tipo: ${getDocumentType(typeFilter)}`);
    }

    if (dateFilter) {
      const formattedDate = new Date(dateFilter).toLocaleDateString("it-IT");
      filters.push(`Data: ${formattedDate}`);
    }

    return filters.length > 0 ? filters.join(", ") : "Filtri";
  };

  const toggleDropdown = () => {
    setFilterDropdownVisible((prev) => !prev);
  };

  const handleApply = () => {
    onApplyFilters();
    setFilterDropdownVisible(false);
  };

  const handleReset = () => {
    onResetFilters();
    setFilterDropdownVisible(false);
  };

  // Funzioni helper per le etichette
  const getStatus = (status: string) => {
    switch (status) {
      case DocumentStatus.OPEN:
        return "Aperto";
      case DocumentStatus.CLOSED:
        return "Completato";
      case DocumentStatus.PENDING:
        return "In Attesa";
    }
    return "";
  };

  const getDocumentType = (type: string) => {
    switch (type) {
      case DocumentType.ACCEPTANCE:
        return "Accettazione";
      case DocumentType.DISPATCH:
        return "Evasione";
    }
    return "";
  };

  return (
    <div
      className="relative"
      ref={dropdownRef}
      style={{ position: "relative" }}
    >
      <label
        htmlFor="filter"
        className="block text-sm font-medium text-gray-700"
      >
        Filtri
      </label>
      <button
        type="button"
        id="filter-button"
        onClick={toggleDropdown}
        className="mt-1 w-full flex items-center justify-between py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      >
        <span>{getFilterSummary()}</span>
        <svg
          className="w-5 h-5 ml-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
          />
        </svg>
      </button>

      {filterDropdownVisible && (
        <div
          ref={dropdownContentRef}
          className="fixed z-50 bg-white rounded-md shadow-lg p-4 border border-gray-200"
          style={{
            width: dropdownRef.current
              ? dropdownRef.current.offsetWidth
              : "auto",
            left: dropdownRef.current
              ? dropdownRef.current.getBoundingClientRect().left
              : 0,
            [dropdownDirection === "down" ? "top" : "bottom"]:
              dropdownDirection === "down"
                ? dropdownRef.current
                  ? dropdownRef.current.getBoundingClientRect().bottom
                  : 0
                : window.innerHeight -
                  (dropdownRef.current
                    ? dropdownRef.current.getBoundingClientRect().top
                    : 0),
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          <div className="space-y-4">
            {/* Status Filter */}
            <div>
              <label
                htmlFor="status-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Stato Documento
              </label>
              <Select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => onStatusChange(e.target.value)}
                className="w-full"
              >
                <option value="">Tutti</option>
                <option value={DocumentStatus.OPEN}>Aperto</option>
                <option value={DocumentStatus.PENDING}>In Attesa</option>
                <option value={DocumentStatus.CLOSED}>Chiuso</option>
              </Select>
            </div>

            {/* Document Type Filter */}
            <div>
              <label
                htmlFor="type-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tipo Documento
              </label>
              <Select
                id="type-filter"
                value={typeFilter}
                onChange={(e) => onTypeChange(e.target.value)}
                className="w-full"
              >
                <option value="">Tutti</option>
                <option value={DocumentType.ACCEPTANCE}>Accettazione</option>
                <option value={DocumentType.DISPATCH}>Evasione</option>
              </Select>
            </div>

            {/* Date Filter */}
            <div>
              <label
                htmlFor="date-filter"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Data
              </label>
              <input
                type="date"
                id="date-filter"
                value={dateFilter}
                onChange={(e) => onDateChange(e.target.value)}
                className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 pt-2 sticky bottom-0 bg-white py-2">
              <button
                type="button"
                onClick={handleReset}
                className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Applica
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentFilters;
